/**
 * Per-zone "likely traffic" detector (spec 2026-08-20). A lap is set aside
 * only when ALL gates pass — one-sided slow, large, spatially coherent,
 * and corroborated by zone time — and never silently: callers must render
 * the setAside naming line wherever the clean-lap % renders.
 */
import { type SpeedTrace } from './speedTraces'
import { binUsableLapValues } from './binSpeedSpreads'
import { median, robustSpread } from './robustStats'
import { timeToleranceMs } from './consistencyPercent'
import { type ZoneLapData } from '../../../zones/TrackZoneTypes'
import { SAMPLE_INTERVAL } from '../../../config/chartDisplay'
import {
  MIN_LAPS_FOR_CONSISTENCY,
  MIN_CURVE_BINS,
  OUTLIER_K,
  ANOMALY_MAGNITUDE_FLOOR_KMH,
  ANOMALY_COVERAGE_FRACTION,
  ANOMALY_MAX_SHARE,
} from '../../../config/consistencyScoring'

export interface ZoneAnomalyResult {
  /** 'traffic-disrupted' = more candidates than the cap allows. */
  status: 'ok' | 'traffic-disrupted'
  /** Set-aside laps (empty when status==='ok' and nothing detected). */
  lapIds: string[]
  /** Same order as lapIds, ascending lap number. */
  lapNumbers: number[]
}

/** Per-lap gate diagnostics (audit/calibration tool — see
 *  scripts/auditTrafficConsistency.ts). One entry per included lap, in the
 *  same order as the `traces` argument's post-exclusion `included` array. */
export interface ZoneAnomalyCandidateDiagnostic {
  lapId: string
  lapNumber: number
  /** Longest contiguous run of "slower than the rest of the field" bins. */
  longestRun: number
  /** Bins with enough field coverage to be gated at all. */
  validBins: number
  /** Largest (restMedian − ownValue) across gated bins — how far off the
   *  worst bin ran, in km/h. Negative means this lap was never the slow one. */
  worstDeltaKmh: number
  /** This lap's zone time minus the field's median zone time, in ms. NaN
   *  when this lap has no zone-time data. */
  timeDeltaMs: number
  /** True only if this lap passed every gate (coherent AND corroborated) —
   *  i.e. it became a set-aside candidate before the per-zone cap is applied. */
  passed: boolean
}

const NO_ANOMALIES: ZoneAnomalyResult = {
  status: 'ok',
  lapIds: [],
  lapNumbers: [],
}

/** Longest run of consecutive `true` values. */
function longestRun(flags: boolean[]): number {
  let best = 0
  let cur = 0
  for (const f of flags) {
    cur = f ? cur + 1 : 0
    if (cur > best) best = cur
  }
  return best
}

interface AnomalyDiagnosticsContext {
  diagnostics: ZoneAnomalyCandidateDiagnostic[]
  includedCount: number
}

/**
 * Shared setup + per-lap gate loop behind both `detectZoneAnomalyLaps` and
 * `explainZoneAnomalyCandidates` — the ONLY place the gate logic lives.
 * Returns null when there isn't enough data to gate anything (too few laps,
 * too few bins, or too few zone times) — both callers treat that the same
 * way they always have (empty result).
 */
function computeZoneAnomalyDiagnostics(args: {
  traces: SpeedTrace[]
  excludedLapIds: Set<string>
  displayBounds: { startDistance: number; endDistance: number }
  lapData: ZoneLapData[]
}): AnomalyDiagnosticsContext | null {
  const { traces, excludedLapIds, displayBounds, lapData } = args
  const included = traces.filter((t) => !excludedLapIds.has(t.id))
  // The rest must stay a scoreable field even after set-aside.
  if (included.length < MIN_LAPS_FOR_CONSISTENCY + 1) return null

  const numBins = Math.floor(
    (displayBounds.endDistance - displayBounds.startDistance) / SAMPLE_INTERVAL
  )
  if (numBins < MIN_CURVE_BINS) return null
  const binValues = binUsableLapValues(
    included,
    displayBounds.startDistance,
    numBins
  )

  const zoneTimeByLapId = new Map(
    lapData
      .filter((l) => !excludedLapIds.has(l.lapId))
      .map((l) => [l.lapId, l.zoneTimeMs])
  )
  const zoneTimes = [...zoneTimeByLapId.values()]
  if (zoneTimes.length < MIN_LAPS_FOR_CONSISTENCY) return null
  const medTime = median(zoneTimes)
  const timeTol = timeToleranceMs(
    displayBounds.endDistance - displayBounds.startDistance
  )

  const diagnostics: ZoneAnomalyCandidateDiagnostic[] = []
  for (let lapIdx = 0; lapIdx < included.length; lapIdx++) {
    const slowFlags: boolean[] = []
    let worstDeltaKmh = -Infinity
    for (const values of binValues) {
      const own = values.find((v) => v.lapIdx === lapIdx)
      const rest = values.filter((v) => v.lapIdx !== lapIdx)
      if (own === undefined || rest.length < MIN_LAPS_FOR_CONSISTENCY) continue
      const restValues = rest.map((v) => v.value)
      const restMed = median(restValues)
      const gate = Math.max(
        ANOMALY_MAGNITUDE_FLOOR_KMH,
        OUTLIER_K * robustSpread(restValues)
      )
      const delta = restMed - own.value
      if (delta > worstDeltaKmh) worstDeltaKmh = delta
      // One-sided: only SLOWER than the field counts.
      slowFlags.push(delta > gate)
    }
    const validBins = slowFlags.length
    const runLength = longestRun(slowFlags)
    const coherent =
      validBins >= MIN_CURVE_BINS &&
      runLength >= ANOMALY_COVERAGE_FRACTION * validBins
    const zoneTime = zoneTimeByLapId.get(included[lapIdx].id)
    const timeDeltaMs = zoneTime !== undefined ? zoneTime - medTime : NaN
    const corroborated = zoneTime !== undefined && zoneTime > medTime + timeTol

    diagnostics.push({
      lapId: included[lapIdx].id,
      lapNumber: included[lapIdx].lapNumber,
      longestRun: runLength,
      validBins,
      worstDeltaKmh: worstDeltaKmh === -Infinity ? 0 : worstDeltaKmh,
      timeDeltaMs,
      passed: coherent && corroborated,
    })
  }

  return { diagnostics, includedCount: included.length }
}

export function detectZoneAnomalyLaps(args: {
  traces: SpeedTrace[]
  excludedLapIds: Set<string>
  displayBounds: { startDistance: number; endDistance: number }
  lapData: ZoneLapData[]
}): ZoneAnomalyResult {
  const ctx = computeZoneAnomalyDiagnostics(args)
  if (!ctx) return NO_ANOMALIES

  const candidates = ctx.diagnostics.filter((d) => d.passed)
  if (candidates.length === 0) return NO_ANOMALIES
  candidates.sort((a, b) => a.lapNumber - b.lapNumber)
  const cap = Math.floor(ctx.includedCount * ANOMALY_MAX_SHARE)
  return {
    status: candidates.length > cap ? 'traffic-disrupted' : 'ok',
    lapIds: candidates.map((c) => c.lapId),
    lapNumbers: candidates.map((c) => c.lapNumber),
  }
}

/**
 * Diagnostic export (audit/calibration tool only — see
 * scripts/auditTrafficConsistency.ts): the per-lap gate numbers behind
 * `detectZoneAnomalyLaps`, for every included lap, so the owner can see WHY
 * a lap did or didn't get set aside while tuning ANOMALY_MAGNITUDE_FLOOR_KMH /
 * ANOMALY_COVERAGE_FRACTION / ANOMALY_MAX_SHARE. Never called from
 * production code paths — the public `ZoneAnomalyResult` shape is unchanged.
 */
export function explainZoneAnomalyCandidates(args: {
  traces: SpeedTrace[]
  excludedLapIds: Set<string>
  displayBounds: { startDistance: number; endDistance: number }
  lapData: ZoneLapData[]
}): ZoneAnomalyCandidateDiagnostic[] {
  return computeZoneAnomalyDiagnostics(args)?.diagnostics ?? []
}
